/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, RefObject } from 'preact';
import { useCallback, useRef } from 'preact/compat';
import { FocusableElement, focusOn, isTabbableElement } from '../utils/PRIVATE_tabbableUtils';
import { FocusTracker, type FocusGuardCallbackDetail } from '../PRIVATE_FocusTracker';

type Props = {
  children: ComponentChildren;
  /**
   * Optional Ref to an element to focus on when component mounts
   * @default - undefined - behavior is to focus on first tabbableElement
   */
  autoFocusRef?: RefObject<{ focus: () => void }>;

  /**
   * Sets whether focus is managed and whether guards are present
   * @default false
   */
  isDisabled?: boolean;

  /**
   * Why a isDisabled prop as compared to ONLY supporting conditional rendering?
   *
   *   1) We need an actual HTML Element for the trap, conditional rendering an element potentially breaks consumer CSS
   *   2) Conditional rendering can short-circuit event bubbling in preact and lead to expected events not bubbling.
   */

  /**
   * Optional Ref to element to focus on when component un-mounts, false to disable.
   * @default - undefined - behavior is to focus on the original activeElement when focus trap was enabled
   */
  restoreFocusRef?: RefObject<{ focus: () => void }> | false;
};

/**
 * Trap focus within a DOM node.  When a user hits Tab or Shift+Tab, focus remains in the focus trap.
 */
export const FocusTrap = ({
  autoFocusRef,
  children,
  isDisabled = false,
  restoreFocusRef
}: Props) => {
  const originalActiveElementRef = useRef<FocusableElement | null>(null);
  const lastActiveElementRef = useRef<FocusableElement | null>(null);

  /**
   * contain focus if trap is enabled, and target is not already contained
   *
   * calling cases for side-effect of setting focus
   *   - on initial mount of (enabled) trap, where target is not inside trap
   *   - on enabling of a disabled trap, where target is not inside trap
   *   - target of click event is outside of trap.
   */
  const conditionallyContainFocus = useCallback(
    (
      hasFocusWithin: boolean,
      currentActiveElement: FocusableElement | null,
      tabbableElements: FocusableElement[]
    ) => {
      //  short-circuit, currentActiveElement not valid
      if (isDisabled || !currentActiveElement) return;

      // just update ref if currentActiveElement is a child of the tracker
      if (hasFocusWithin) {
        lastActiveElementRef.current = currentActiveElement;
        return;
      }

      // set focus to last active tab-stop if lastElement is defined and inside trap
      if (lastActiveElementRef.current) {
        focusOn(lastActiveElementRef.current);
      } else if (tabbableElements.length > 0) {
        // otherwise just contain focus within trap as target is outside enabled trap and we have no last-known tab-stop
        focusOn(tabbableElements[0]);
      }
    },
    [isDisabled]
  );

  // loop focus from start edge
  const handleFocusStartEdge = useCallback((detail: FocusGuardCallbackDetail) => {
    const tabbableCount = detail.tabbableElements.length;
    if (tabbableCount > 0) {
      focusOn(detail.tabbableElements[tabbableCount - 1]);
    }
  }, []);

  // loop focus from end edge
  const handleFocusEndEdge = useCallback((detail: FocusGuardCallbackDetail) => {
    const tabbableCount = detail.tabbableElements.length;
    if (tabbableCount > 0) {
      focusOn(detail.tabbableElements[0]);
    }
  }, []);

  // handle enable case and initial render
  const handleStartTracking = useCallback(
    (detail: {
      activeElement: FocusableElement;
      hasFocusWithin: boolean;
      tabbableElements: FocusableElement[];
    }) => {
      originalActiveElementRef.current = detail.activeElement;

      // init focus
      if (autoFocusRef && autoFocusRef.current) {
        focusOn(autoFocusRef.current);
      } else {
        conditionallyContainFocus(
          detail.hasFocusWithin,
          detail.activeElement,
          detail.tabbableElements
        );
      }
    },
    [autoFocusRef, conditionallyContainFocus]
  );

  // handle disable case
  const handleStopTracking = useCallback(
    (detail: { hasFocusWithin: boolean; lastActiveElement: FocusableElement | null }) => {
      lastActiveElementRef.current = detail.lastActiveElement;
      if (restoreFocusRef !== false) {
        restoreFocusForCleanup(restoreFocusRef?.current || originalActiveElementRef.current);
      }
    },
    [restoreFocusRef]
  );

  return (
    <FocusTracker
      isDisabled={isDisabled}
      onStartTracking={handleStartTracking}
      onStopTracking={handleStopTracking}
      onFocusStartEdge={handleFocusStartEdge}
      onFocusEndEdge={handleFocusEndEdge}>
      {children}
    </FocusTracker>
  );
};

// setTimeout ensures that unmount lifecycle has completed
const restoreFocusForCleanup = (focusTarget: { focus: () => void } | null) => {
  if (focusTarget) {
    const isElement = focusTarget instanceof HTMLElement || focusTarget instanceof SVGElement;
    if (!isElement || isTabbableElement(focusTarget)) {
      setTimeout(() => {
        focusOn(focusTarget);
      }, 0);
    }
  }
};
